import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { fetchProjectsForPlaneamiento, nombreProyectoDesdeLista } from '../../../utils/projectsFromApi';
import { getMatrizResuelta, formatearRangoFechas, etiquetaSemana } from '../../../data/mockCiclosCiclicos';
import { fetchCiclico, fetchDatosPlaneamientoCiclicos } from '../../../services/ciclicoService';
import concesionariaAreaDespachoService from '../../../services/concesionariaAreaDespachoService';
import {
  ESTADO_SALIDA,
  parseLineasCelda,
  fechasPorDiaDesdeInicio,
  formatearDiaCorto,
  buscarRecetaPlatoEnCatalogo,
  slotKey,
  loadSalidasProduccion,
  getSalidaPorCicloYDia,
  esDiaKeyValido,
  DIAS_LABEL,
} from '../../../data/mockSalidasProduccion';
import {
  loadDespachoReparto,
  getRepartoPorCicloYDia,
  upsertDespachoReparto,
  sumaAsignacionesSlot,
  repartoEquitativo,
} from '../../../data/mockDespachoReparto';

const basePath = '/produccion-concesionaria/operaciones/despacho';

function parseQty(s) {
  return Math.max(0, parseInt(String(s || '').replace(/\D/g, ''), 10) || 0);
}

const DespachoDetalle = () => {
  const { cicloId: cicloIdParam, diaKey: diaKeyParam } = useParams();
  const cicloId = cicloIdParam ? decodeURIComponent(cicloIdParam) : '';
  const diaKey = diaKeyParam ? decodeURIComponent(diaKeyParam).toLowerCase() : '';

  const [ciclo, setCiclo] = useState(null);
  const [cicloCargando, setCicloCargando] = useState(true);
  const [plantillas, setPlantillas] = useState([]);
  const [tiempos, setTiempos] = useState([]);
  const [tiposPlato, setTiposPlato] = useState([]);
  const [proyectosCatalog, setProyectosCatalog] = useState([]);
  const [recetasCatalogo, setRecetasCatalogo] = useState([]);
  const [areas, setAreas] = useState([]);
  const [areasCargando, setAreasCargando] = useState(true);

  useEffect(() => {
    let ok = true;
    (async () => {
      if (!cicloId) {
        setCiclo(null);
        setCicloCargando(false);
        return;
      }
      setCicloCargando(true);
      setCiclo(null);
      try {
        const [bundle, proj] = await Promise.all([fetchDatosPlaneamientoCiclicos(), fetchProjectsForPlaneamiento()]);
        if (!ok) return;
        setProyectosCatalog(proj);
        setPlantillas(bundle.plantillas || []);
        setTiempos((bundle.tiempos || []).filter((x) => x.active !== false));
        setTiposPlato((bundle.tiposPlato || []).filter((x) => x.active !== false));
        setRecetasCatalogo(Array.isArray(bundle.recetas) ? bundle.recetas : []);
        const enBundle = (bundle.ciclos || []).find((c) => String(c.id) === String(cicloId));
        if (enBundle) {
          setCiclo(enBundle);
        } else {
          const c = await fetchCiclico(cicloId);
          if (ok) setCiclo(c);
        }
      } catch {
        if (!ok) return;
        setPlantillas([]);
        setTiempos([]);
        setTiposPlato([]);
        setRecetasCatalogo([]);
        try {
          const [proj, c] = await Promise.all([fetchProjectsForPlaneamiento(), fetchCiclico(cicloId)]);
          if (ok) {
            setProyectosCatalog(proj);
            setCiclo(c);
          }
        } catch {
          if (ok) {
            setProyectosCatalog([]);
            setCiclo(null);
          }
        }
      } finally {
        if (ok) setCicloCargando(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [cicloId]);

  const nombreProyecto = useCallback(
    (pid) => nombreProyectoDesdeLista(pid, proyectosCatalog),
    [proyectosCatalog],
  );

  const plantilla = useMemo(
    () => plantillas.find((p) => String(p.id) === String(ciclo?.plantillaId)) || null,
    [plantillas, ciclo],
  );

  const { matriz, grupos } = useMemo(() => {
    if (!ciclo || !plantilla) return { matriz: {}, grupos: [] };
    return getMatrizResuelta(ciclo, plantilla);
  }, [ciclo, plantilla]);

  const fechasDia = useMemo(() => {
    if (!ciclo?.fechaInicio) return {};
    return fechasPorDiaDesdeInicio(ciclo.fechaInicio);
  }, [ciclo]);

  const tiempoLabel = useCallback(
    (id) => tiempos.find((t) => String(t.id) === String(id))?.label || id,
    [tiempos],
  );
  const tipoLabel = useCallback(
    (id) => tiposPlato.find((t) => String(t.id) === String(id))?.name || id,
    [tiposPlato],
  );

  const salida = useMemo(() => {
    if (!cicloId || !esDiaKeyValido(diaKey)) return null;
    return getSalidaPorCicloYDia(cicloId, diaKey, loadSalidasProduccion());
  }, [cicloId, diaKey]);

  const slots = useMemo(() => {
    if (!plantilla || !grupos.length || !esDiaKeyValido(diaKey) || !plantilla.diasSemana?.[diaKey]) {
      return [];
    }
    const rows = [];
    grupos.forEach((g) => {
      const tLabel = tiempoLabel(g.tiempoId);
      g.filas.forEach((f) => {
        const texto = matriz[diaKey]?.[f.rowKey] ?? '';
        rows.push({
          rowKey: f.rowKey,
          tiempoId: g.tiempoId,
          tiempoLabel: tLabel,
          tipoPlatoId: f.tipoPlatoId,
          tipoLabel: tipoLabel(f.tipoPlatoId),
          texto,
          sk: slotKey(diaKey, f.rowKey),
        });
      });
    });
    return rows;
  }, [plantilla, grupos, matriz, diaKey, tiempoLabel, tipoLabel]);

  const cantidadesSalida = useMemo(() => {
    const c = salida?.cantidades && typeof salida.cantidades === 'object' ? salida.cantidades : {};
    return { ...c };
  }, [salida]);

  const slotsConStock = useMemo(() => {
    return slots.filter((s) => parseQty(cantidadesSalida[s.sk]) > 0);
  }, [slots, cantidadesSalida]);

  const proyectoIdCiclo = ciclo?.proyectoId != null ? String(ciclo.proyectoId) : '';

  useEffect(() => {
    let cancel = false;
    setAreasCargando(true);
    (async () => {
      try {
        const res = await concesionariaAreaDespachoService.getAreas();
        if (cancel) return;
        const raw = res?.success ? res.data || [] : [];
        const filtradas = raw.filter((a) => {
          if (a.is_active === false) return false;
          const pid = a.project_id != null ? String(a.project_id) : '';
          if (!proyectoIdCiclo) return false;
          return pid === proyectoIdCiclo;
        });
        setAreas([...filtradas].sort((x, y) => (x.sort_order || 0) - (y.sort_order || 0) || String(x.name).localeCompare(String(y.name), 'es')));
      } catch {
        if (!cancel) setAreas([]);
      } finally {
        if (!cancel) setAreasCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [proyectoIdCiclo]);

  const areaIds = useMemo(() => areas.map((a) => String(a.id)), [areas]);

  const [asignaciones, setAsignaciones] = useState({});

  useEffect(() => {
    if (!cicloId || !esDiaKeyValido(diaKey) || !slotsConStock.length || !areaIds.length) {
      setAsignaciones({});
      return;
    }
    const rec = getRepartoPorCicloYDia(cicloId, diaKey, loadDespachoReparto());
    const next = {};
    for (const slot of slotsConStock) {
      next[slot.sk] = {};
      for (const aid of areaIds) {
        const stored = rec?.porSlot?.[slot.sk]?.[aid];
        next[slot.sk][aid] = stored != null && stored !== '' ? String(Math.max(0, parseInt(String(stored), 10) || 0)) : '';
      }
    }
    setAsignaciones(next);
  }, [cicloId, diaKey, slotsConStock, areaIds]);

  const setCantidadArea = (sk, areaId, value) => {
    const cleaned = String(value).replace(/[^\d]/g, '');
    setAsignaciones((prev) => ({
      ...prev,
      [sk]: {
        ...(prev[sk] || {}),
        [String(areaId)]: cleaned,
      },
    }));
  };

  const porSlotDesdeAsignaciones = useMemo(() => {
    const porSlot = {};
    for (const slot of slotsConStock) {
      const byArea = {};
      for (const aid of areaIds) {
        const n = parseQty(asignaciones[slot.sk]?.[aid]);
        if (n > 0) byArea[aid] = n;
      }
      porSlot[slot.sk] = byArea;
    }
    return porSlot;
  }, [slotsConStock, areaIds, asignaciones]);

  const sumaAsignadaPorSlot = useMemo(() => {
    const out = {};
    for (const slot of slotsConStock) {
      out[slot.sk] = sumaAsignacionesSlot(porSlotDesdeAsignaciones, slot.sk, areaIds);
    }
    return out;
  }, [slotsConStock, porSlotDesdeAsignaciones, areaIds]);

  const [toast, setToast] = useState(null);

  const guardar = () => {
    if (!cicloId || !esDiaKeyValido(diaKey)) return;
    upsertDespachoReparto({ cicloId, diaKey, porSlot: porSlotDesdeAsignaciones });
    setToast({ type: 'ok', message: 'Reparto guardado.' });
    setTimeout(() => setToast(null), 3000);
  };

  const distribuirEquitativo = () => {
    if (!areaIds.length) return;
    setAsignaciones((prev) => {
      const next = { ...prev };
      for (const slot of slotsConStock) {
        const total = parseQty(cantidadesSalida[slot.sk]);
        const eq = repartoEquitativo(total, areaIds);
        next[slot.sk] = {};
        for (const aid of areaIds) {
          next[slot.sk][aid] = String(eq[aid] ?? 0);
        }
      }
      return next;
    });
    setToast({ type: 'ok', message: 'Cantidades repartidas de forma equitativa entre áreas.' });
    setTimeout(() => setToast(null), 3000);
  };

  const slotsPorTiempo = useMemo(() => {
    const m = new Map();
    for (const s of slotsConStock) {
      const k = String(s.tiempoId);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    return m;
  }, [slotsConStock]);

  const ymd = fechasDia[diaKey];
  const diaNombre = DIAS_LABEL[diaKey] || diaKey;

  if (!cicloId || !esDiaKeyValido(diaKey)) {
    return <Navigate to={basePath} replace />;
  }

  if (!cicloCargando && (!ciclo || !plantilla)) {
    return <Navigate to={basePath} replace />;
  }

  if (!cicloCargando && ciclo && plantilla && (!salida || salida.estado !== ESTADO_SALIDA.SALIDA_PRODUCCION)) {
    return <Navigate to={basePath} replace />;
  }

  const diaValidoEnPlantilla = plantilla?.diasSemana?.[diaKey];

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full max-w-[1200px] mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <Link to={basePath} className="hover:text-teal-600">
          Operaciones / Despacho
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Detalle</span>
      </nav>

      {cicloCargando ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">Cargando…</p>
      ) : !diaValidoEnPlantilla ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Este día no está activo en la plantilla del cíclico.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Despacho del día</h1>
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium text-gray-800">{ciclo?.nombre}</span>
                <span className="mx-2 text-gray-400">·</span>
                {etiquetaSemana(ciclo?.anio, ciclo?.numeroSemana)}
                <span className="mx-2 text-gray-400">·</span>
                {formatearRangoFechas(ciclo?.fechaInicio, ciclo?.fechaFin)}
              </p>
              <p className="mt-2 text-sm text-gray-700">
                <strong>{diaNombre}</strong>
                {ymd ? (
                  <>
                    {' '}
                    <span className="text-gray-500">({ymd}</span>
                    <span className="text-gray-500"> · {formatearDiaCorto(ymd)})</span>
                  </>
                ) : null}
                <span className="mx-2 text-gray-400">·</span>
                <span className="text-gray-500">Proyecto:</span>{' '}
                <span className="font-medium">{nombreProyecto(proyectoIdCiclo)}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Link
                to={basePath}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Volver al listado
              </Link>
              <Link
                to={`/produccion-concesionaria/operaciones/salidas-produccion/ciclo/${encodeURIComponent(cicloId)}/dia/${encodeURIComponent(diaKey)}`}
                className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-800 hover:bg-teal-100"
              >
                Ver salida a producción
              </Link>
            </div>
          </div>

          {toast && (
            <div
              className={`mt-4 rounded-md border px-4 py-2 text-sm ${
                toast.type === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-gray-200 bg-gray-50 text-gray-800'
              }`}
            >
              {toast.message}
            </div>
          )}

          {areasCargando ? (
            <p className="mt-6 text-sm text-gray-600">Cargando áreas de despacho…</p>
          ) : areas.length === 0 ? (
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              No hay <strong>áreas de despacho</strong> activas vinculadas al proyecto de este cíclico. Configúrelas en{' '}
              <Link
                to="/produccion-concesionaria/configuraciones/area-despacho"
                className="font-medium underline text-amber-950"
              >
                Configuración → Área de despacho
              </Link>{' '}
              (mismo proyecto u operación que el cíclico).
            </div>
          ) : slotsConStock.length === 0 ? (
            <p className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              No hay cantidades registradas en la salida a producción para este día. Use la pantalla de salidas para
              cargar cantidades antes de repartir.
            </p>
          ) : (
            <>
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={guardar}
                  className="inline-flex rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-sky-700"
                >
                  Guardar reparto
                </button>
                <button
                  type="button"
                  onClick={distribuirEquitativo}
                  className="inline-flex rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Distribuir equitativamente
                </button>
              </div>

              <p className="mt-3 text-xs text-gray-500">
                El <strong>stock a repartir</strong> por celda es la cantidad confirmada en{' '}
                <strong>Salida a producción</strong> para ese día. La suma por celda en todas las áreas no debería
                superar ese total; el objetivo es completar el reparto hasta cubrir todo lo producido.
              </p>

              <div className="mt-8 flex flex-col gap-10">
                {[...slotsPorTiempo.entries()].map(([tiempoId, listaSlots]) => (
                  <section key={tiempoId} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 sm:px-6 py-3 bg-sky-50/80 border-b border-gray-100">
                      <h2 className="text-lg font-semibold text-gray-900">{tiempoLabel(tiempoId)}</h2>
                    </div>
                    <div className="p-4 sm:p-6 flex flex-col gap-8">
                      {areas.map((area) => (
                        <div key={area.id} className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                          <h3 className="text-base font-semibold text-gray-800 mb-4">
                            Área de despacho: <span className="text-sky-900">{area.name}</span>
                          </h3>
                          <div className="space-y-4">
                            {listaSlots.map((slot) => {
                              const stockTotal = parseQty(cantidadesSalida[slot.sk]);
                              const suma = sumaAsignadaPorSlot[slot.sk] ?? 0;
                              const ok = suma === stockTotal && stockTotal > 0;
                              return (
                                <div
                                  key={`${slot.sk}-${area.id}`}
                                  className="rounded-md border border-white bg-white p-3 shadow-sm"
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-semibold uppercase text-gray-500">Tipo de plato</p>
                                      <p className="text-sm font-medium text-gray-900">{slot.tipoLabel}</p>
                                      <p className="mt-2 text-xs font-semibold uppercase text-gray-500">Plato (menú)</p>
                                      <p className="text-sm text-gray-800 break-words">{slot.texto || '—'}</p>
                                      {parseLineasCelda(slot.texto).length > 1 && (
                                        <p className="mt-1 text-xs text-gray-500">
                                          Varios platos en la celda; la cantidad de salida aplica al menú completo.
                                        </p>
                                      )}
                                      {(() => {
                                        const partes = parseLineasCelda(slot.texto);
                                        if (partes.length <= 1) return null;
                                        return (
                                          <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                                            {partes.map((nombre) => {
                                              const r = buscarRecetaPlatoEnCatalogo(nombre, recetasCatalogo);
                                              return (
                                                <li key={nombre}>
                                                  <span className="font-medium">{nombre}</span>
                                                  {r?.name && r.name !== nombre ? ` → ${r.name}` : ''}
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        );
                                      })()}
                                    </div>
                                    <div className="shrink-0 w-full sm:w-40">
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Cantidad para esta área
                                      </label>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        value={asignaciones[slot.sk]?.[String(area.id)] ?? ''}
                                        onChange={(e) => setCantidadArea(slot.sk, area.id, e.target.value)}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm tabular-nums focus:border-sky-500 focus:ring-sky-500"
                                        placeholder="0"
                                      />
                                    </div>
                                  </div>
                                  {area.id === areas[0]?.id ? (
                                    <div
                                      className={`mt-3 flex flex-wrap items-center gap-2 text-xs rounded-md px-2 py-1.5 ${
                                        suma > stockTotal
                                          ? 'bg-red-50 text-red-800 border border-red-100'
                                          : ok
                                            ? 'bg-emerald-50 text-emerald-900 border border-emerald-100'
                                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                                      }`}
                                    >
                                      <span>
                                        Stock salida (total celda): <strong>{stockTotal}</strong>
                                      </span>
                                      <span className="text-gray-400">|</span>
                                      <span>
                                        Asignado en áreas: <strong>{suma}</strong>
                                      </span>
                                      <span className="text-gray-400">|</span>
                                      <span>
                                        Pendiente: <strong>{Math.max(0, stockTotal - suma)}</strong>
                                      </span>
                                      {suma > stockTotal && <span className="font-medium"> · Excede el total</span>}
                                      {ok && <span className="font-medium"> · Reparto completo</span>}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DespachoDetalle;
